import React, { memo } from "react";
import type { ReactNode } from "react";

import LayoutColapse from "src-next/shared/ui/layouts/LayoutColapse";
import type { SharedMenuItem } from "src-next/shared/ui/menuTypes";

import { useLegacyNavigationModel } from "@/navigation/pageRegistry/useLegacyNavigationModel";
import { LayoutPrivateHeaderContent } from "./components/Header";
import { HeaderCollapseProvider } from "./components/Header/HeaderCollapseContext";
import LegacySharedSidebar from "./components/Drawer/LegacySharedSidebar";
import { MobileBarNavigation } from "./components/MobileBarNavigation";
import { ChatLuxorProvider } from "@/components/common/ChatLuxor";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import { currentFeatures } from "@/configs/Settings";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import fluxmedHorizontalLogo from "@/assets/fluxmed-logo-horizontal-white.png";
import Box from "@mui/material/Box";

import type { SharedHeaderProps } from "src-next/shared/ui/layouts/SharedHeader";

/**
 * Converte `IFeatures` (legado) em `SharedMenuItem[]` (layout refatorado).
 *
 * Mantém o agrupamento atual do legado via `parent`:
 * - itens sem `parent` viram roots
 * - itens com `parent` viram children do root cujo `text` corresponde
 */
function buildSharedMenuItemsFromLegacyFeatures(
  features: any[],
): SharedMenuItem[] {
  const rootItems = features.filter((feature) => !feature.parent);

  return rootItems.map((rootFeature) => {
    const children = features
      .filter((child) => child.parent === rootFeature.text)
      .sort(
        (a: any, b: any) => (a?.sideMenuIndex ?? 0) - (b?.sideMenuIndex ?? 0),
      )
      .map((childFeature) => ({
        id: `legacy-${childFeature.path}`,
        label: childFeature.text,
        icon: childFeature.icon
          ? React.createElement(childFeature.icon)
          : undefined,
        path: childFeature.path,
      }));

    return {
      id: `legacy-${rootFeature.path}`,
      label: rootFeature.text,
      icon: rootFeature.icon
        ? React.createElement(rootFeature.icon)
        : undefined,
      path: rootFeature.path,
      children: children.length > 0 ? children : undefined,
    };
  });
}

const FluxmedLogoImage = () => (
  <Box
    component="img"
    src={fluxmedHorizontalLogo}
    alt="Fluxmed"
    sx={{ height: 28, width: "auto", objectFit: "contain" }}
  />
);

/**
 * Header V2:
 * - Reaproveita o header do legado (LayoutPrivateHeaderContent)
 * - Adapta o contrato para `SharedHeaderProps` do LayoutColapse.
 *
 * Observação:
 * - O `LayoutPrivateHeaderContent` já contém AvatarMenu, notificações, etc.
 * - Aqui apenas conectamos o botão de menu ao estado `menuOpen` do LayoutColapse.
 */
const LegacyHeaderV2 = memo<SharedHeaderProps>(({ menuOpen, onToggleMenu }) => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));

  const [avatarMenuAnchorEl, setAvatarMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAvatarMenuAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    onToggleMenu(!menuOpen);
  };

  return (
    <LayoutPrivateHeaderContent
      isMediumScreen={isMediumScreen}
      handleDrawerToggle={handleDrawerToggle}
      avatarMenuAnchorEl={avatarMenuAnchorEl}
      handleMenuOpen={handleMenuOpen}
      handleMenuClose={handleMenuClose}
      isSidebarOpen={menuOpen}
    />
  );
});

LegacyHeaderV2.displayName = "LegacyHeaderV2";

export interface PrivateMainLayoutV2Props {
  children: ReactNode;
}

/**
 * PrivateMainLayoutV2
 *
 * Nova versão de layout do legado baseada no padrão do `src-next`:
 * - `LayoutColapse` + `SharedSidebar` (default) + header customizado
 * - menu derivado do `pageRegistry` (via `useLegacyNavigationModel`)
 *
 * Importante:
 * - Este layout deve ser introduzido por flag para reduzir risco.
 * - Nesta etapa, mantemos as páginas ainda vivendo em `src/` (legado).
 */
const PrivateMainLayoutV2 = memo<PrivateMainLayoutV2Props>(({ children }) => {
  const { sideBarItens } = useLegacyNavigationModel();
  const { isSidebarExpanded, setIsSidebarExpanded } = useSidebarNavigation();

  const menuItems = buildSharedMenuItemsFromLegacyFeatures(sideBarItens);
  const isRegisterMode = currentFeatures.register;

  const layoutShell = (
    <>
      <LayoutColapse
        logo={<FluxmedLogoImage />}
        title=""
        menuItems={menuItems}
        HeaderComponent={LegacyHeaderV2}
        SidebarComponent={LegacySharedSidebar}
        navigationExpanded={isRegisterMode ? false : isSidebarExpanded}
        onNavigationExpandedChange={
          isRegisterMode ? () => undefined : setIsSidebarExpanded
        }
      >
        {children}
      </LayoutColapse>
      <MobileBarNavigation />
    </>
  );

  return (
    <HeaderCollapseProvider>
      {currentFeatures.defaultApp ? (
        <ChatLuxorProvider>{layoutShell}</ChatLuxorProvider>
      ) : (
        layoutShell
      )}
    </HeaderCollapseProvider>
  );
});

PrivateMainLayoutV2.displayName = "PrivateMainLayoutV2";

export { PrivateMainLayoutV2 };
